import { thousandFormat } from 'src/app/constants/formatters';

const BILLION = 1000000000;
const MILLION = 1000000;
const THOUSAND = 1000;
const ONE_DECIMAL = 1;
const TWO_DECIMALS = 2;
const PERCENTAGE_DECIMAL = 0;

/**
 * Revenue steps up a unit at each thousand-fold so the headline stays short and readable:
 * "Rp150,000", "Rp850 juta", "Rp1.18 miliar".
 *
 * The step only happens once the value actually reaches that unit — "Rp0.15 juta" is harder to read
 * than "Rp150,000", and "Rp0.85 miliar" harder than "Rp850 juta".
 *
 * Billions carry two decimals because one would collapse a Rp1.175 billion month to "Rp1.2 miliar"
 * and throw away tens of millions of rupiah of precision.
 */
export const toJutaFormat = (value: number): string => {
  if (!isFinite(value)) {
    return 'Rp0';
  }
  const magnitude = Math.abs(value);

  // Number() drops trailing zeros so a round figure reads "Rp850 juta", not "Rp850.0 juta".
  if (magnitude >= BILLION) {
    return `Rp${thousandFormat(Number((value / BILLION).toFixed(TWO_DECIMALS)))} miliar`;
  }
  if (magnitude >= MILLION) {
    return `Rp${thousandFormat(Number((value / MILLION).toFixed(ONE_DECIMAL)))} juta`;
  }
  return `Rp${thousandFormat(Math.round(value))}`;
};

/**
 * The full rupiah figure, unrounded.
 *
 * Shown alongside the abbreviated headline: "Rp1.18 miliar" rounds to the nearest ten million, so
 * anyone asked to justify the number needs the exact one visible without hovering or exporting.
 */
export const toExactRupiahFormat = (value: number): string =>
  isFinite(value) ? `Rp${thousandFormat(Math.round(value))}` : 'Rp0';

export interface RevenueScale {
  divisor: number;
  unitLabel: string;
}

/**
 * A chart axis has to commit to one unit for every point, so the unit is picked from the largest
 * value in the series. Without this a series topping out at Rp260,000 renders as "0.26" against an
 * axis labelled "Rp juta", and a billion-rupiah month renders as an unreadable four-digit "juta".
 */
export const getRevenueScale = (maxValue: number): RevenueScale => {
  if (maxValue >= BILLION) {
    return { divisor: BILLION, unitLabel: 'miliar' };
  }
  if (maxValue >= MILLION) {
    return { divisor: MILLION, unitLabel: 'juta' };
  }
  return { divisor: THOUSAND, unitLabel: 'ribu' };
};

export const toCountFormat = (value: number): string => thousandFormat(Math.round(value));

export const toPercentageFormat = (value: number, decimal: number = PERCENTAGE_DECIMAL): string =>
  `${value.toFixed(decimal)}%`;

/**
 * A delta is null when the previous period had no data — showing "0%" there would imply
 * "unchanged", which is a different and wrong claim.
 */
export const toDeltaFormat = (deltaPercentage: number | null): string => {
  if (deltaPercentage === null || !isFinite(deltaPercentage)) {
    return '-';
  }
  const sign = deltaPercentage > 0 ? '↑' : deltaPercentage < 0 ? '↓' : '';
  return `${sign} ${Math.abs(deltaPercentage).toFixed(PERCENTAGE_DECIMAL)}%`;
};

export const getDeltaModifier = (deltaPercentage: number | null): string => {
  if (deltaPercentage === null || deltaPercentage === 0) {
    return 'neutral';
  }
  return deltaPercentage > 0 ? 'up' : 'down';
};
